import React, { useRef, useState } from 'react'
import cx from 'classnames'
import result from 'lodash/result'
import { color, type IconComponent, Icon as Icons, l, Modal } from '@manychat/manyui'

import { withErrorBoundary } from 'common/core/components/ErrorBoundary'
import {
  NotificationItem,
  NotificationRemoveType,
} from 'common/core/components/NotifyBar/NotifyInterfaces'
import { NotificationBarType } from 'common/core/constants/NotificationBarType'
import { useToggle } from 'common/hooks/useToggle'
import { NotificationDisplayError } from 'shared/errors/clientLayerError'
import errorTrackingService from 'utils/services/errorTrackingService'

import NotificationContent from './NotificationContent'

import cm from './Notification.module.css'

const ICON_TYPES: Record<NotificationBarType, IconComponent> = {
  [NotificationBarType.WARNING]: Icons.Warning,
  [NotificationBarType.ERROR]: Icons.Warning,
  [NotificationBarType.SUCCESS]: Icons.CheckCircle,
  [NotificationBarType.NORMAL]: Icons.Info,
  [NotificationBarType.INFO]: Icons.Info,
  [NotificationBarType.DEFAULT]: Icons.Info,
}

const BG_COLORS = {
  [NotificationBarType.SUCCESS]: color.background.successDefault,
  [NotificationBarType.ERROR]: color.background.dangerDefault,
  [NotificationBarType.WARNING]: color.background.warningDefault,
  [NotificationBarType.INFO]: color.background.informationDefault,
  [NotificationBarType.NORMAL]: color.background.informationDefault,
  [NotificationBarType.DEFAULT]: color.background.informationDefault,
}

const ICON_COLORS = {
  [NotificationBarType.WARNING]: color.icon.warning,
  [NotificationBarType.ERROR]: color.icon.danger,
  [NotificationBarType.SUCCESS]: color.icon.success,
  [NotificationBarType.INFO]: color.icon.information,
  [NotificationBarType.DEFAULT]: color.icon.information,
  [NotificationBarType.NORMAL]: color.icon.information,
}

export const NOTIFICATION_HEIGHT = 51

interface NotifyBar {
  notification: NotificationItem
  onClose: (notification: NotificationItem) => void
}

const Notification = ({ notification, onClose }: NotifyBar) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const { state, toggle: toggleModal } = useToggle()
  const [isOverflow, setIsOverflow] = useState(false)
  const { type: notificationType = NotificationBarType.DEFAULT } = notification
  const content = result(notification, 'render', null) ?? result(notification, 'message', '')
  const isValidElement = React.isValidElement(content)
  const CustomIcon = notification?.customIconName ? Icons[notification?.customIconName] : null
  const NotificationIcon = CustomIcon ?? ICON_TYPES[notificationType]

  React.useEffect(() => {
    const content = ref.current?.children?.[0]
    setIsOverflow(Boolean(content && content.scrollWidth > content.clientWidth))
  }, [ref])

  if (!isValidElement && !content) {
    errorTrackingService.logError(new NotificationDisplayError(), {
      scope: 'layout',
      section: 'notification_error_display_notification',
      extra: { notification },
    })
    return null
  }

  const isRemovable = notification.removeType !== NotificationRemoveType.NEVER
  const isShownReadMore = isOverflow && !notification.isWithoutReadMore

  const handleClose = () => onClose(notification)

  return (
    <>
      <div aria-label={l.translate('Static Notification')} className={cm.wrapper}>
        <span
          className={cx(
            cm.iconWrapper,
            'flex-shrink-0 align-center justify-center d-flex m-r-xs rounded-circle',
          )}
          style={{ backgroundColor: BG_COLORS[notificationType] }}
        >
          {NotificationIcon && (
            <NotificationIcon size={16} style={{ color: ICON_COLORS[notificationType] }} />
          )}
        </span>
        <div ref={ref} className={cx(cm.content, 'd-flex align-center flex-grow-1 text-body')}>
          <NotificationContent content={content} />
        </div>
        {isShownReadMore && (
          <a className={cm.more} onClick={toggleModal}>
            {l.translate('Read More')}
          </a>
        )}
        {isRemovable && (
          <Icons.Close
            size={24}
            onClick={handleClose}
            className={cx(cm.close, 'm-x-sm m-y-xxs')}
            data-test-id="notification-close"
          />
        )}
      </div>

      <Modal title="Notification Details" open={state === 'on'} onRequestClose={toggleModal}>
        <div className="p-x-xl p-b-xl p-t-md" style={{ lineHeight: '24px' }}>
          <NotificationContent content={content} />
        </div>
      </Modal>
    </>
  )
}

export default withErrorBoundary(Notification, {
  scope: 'layout',
  section: 'notifications',
  fallbackComponent: () => null,
})
